/**
 * 格式化日期
 * @param {number|string|Date} timestamp 时间戳或日期
 * @param {string} format 格式化模板
 * @returns {string}
 */
export function formatDate(timestamp, format = 'YYYY-MM-DD HH:mm:ss') {
  if (!timestamp) return ''
  
  let date
  if (typeof timestamp === 'number') {
    // 如果是10位时间戳，转换为毫秒
    date = new Date(timestamp < 10000000000 ? timestamp * 1000 : timestamp)
  } else if (typeof timestamp === 'string') {
    date = new Date(timestamp)
  } else {
    date = timestamp
  }

  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  const seconds = String(date.getSeconds()).padStart(2, '0')

  return format
    .replace('YYYY', year)
    .replace('MM', month)
    .replace('DD', day)
    .replace('HH', hours)
    .replace('mm', minutes)
    .replace('ss', seconds)
}

/**
 * 格式化时间显示
 * @param {string} time 时间字符串 HH:mm:ss
 * @returns {string}
 */
export function formatTime(time) {
  if (!time) return ''
  return time.substring(0, 5)
}

/**
 * 格式化金额
 * @param {number|string} amount 金额
 * @param {number} decimals 小数位数
 * @returns {string}
 */
export function formatMoney(amount, decimals = 2) {
  if (!amount && amount !== 0) return ''
  return Number(amount).toFixed(decimals)
}

/**
 * 获取文件大小描述
 * @param {number} bytes 字节数
 * @returns {string}
 */
export function formatFileSize(bytes) {
  if (!bytes) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let i = 0
  while (bytes >= 1024 && i < units.length - 1) {
    bytes /= 1024
    i++
  }
  return bytes.toFixed(2) + ' ' + units[i]
}

/**
 * 获取支付状态描述
 * @param {number} status 状态码
 * @returns {string}
 */
export function getPayStatusText(status) {
  const statusMap = {
    0: '未支付',
    1: '已支付',
    2: '已退款'
  }
  return statusMap[status] || '未知'
}

/**
 * 获取摘要审核状态描述
 * @param {number} status 状态码
 * @returns {string}
 */
export function getReviewStatusText(status) {
  const statusMap = {
    0: '待审核',
    1: '审核通过',
    2: '审核拒绝'
  }
  return statusMap[status] || '未知'
}

/**
 * 获取摘要审核状态类型
 * @param {number} status 状态码
 * @returns {string}
 */
export function getReviewStatusType(status) {
  const typeMap = {
    0: 'warning',
    1: 'success',
    2: 'danger'
  }
  return typeMap[status] || 'info'
}

/**
 * 获取支付状态类型
 * @param {number} status 状态码
 * @returns {string}
 */
export function getPayStatusType(status) {
  const typeMap = {
    0: 'warning',
    1: 'success',
    2: 'info'
  }
  return typeMap[status] || 'info'
}

/**
 * 存储用户信息
 * @param {object} userInfo 用户信息
 */
export function setUserInfo(userInfo) {
  localStorage.setItem('userInfo', JSON.stringify(userInfo))
}

/**
 * 获取用户信息
 * @returns {object|null}
 */
export function getUserInfo() {
  const userInfo = localStorage.getItem('userInfo')
  return userInfo ? JSON.parse(userInfo) : null
}

/**
 * 清除用户信息
 */
export function clearUserInfo() {
  localStorage.removeItem('token')
  localStorage.removeItem('userInfo')
}

/**
 * 检查是否登录
 * @returns {boolean}
 */
export function isLoggedIn() {
  return !!localStorage.getItem('token')
}

/**
 * 生成随机字符串
 * @param {number} length 长度
 * @returns {string}
 */
export function randomString(length = 32) {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
  let result = ''
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return result
}

/**
 * 防抖函数
 * @param {Function} fn 函数
 * @param {number} delay 延迟时间
 * @returns {Function}
 */
export function debounce(fn, delay = 300) {
  let timer = null
  return function(...args) {
    if (timer) clearTimeout(timer)
    timer = setTimeout(() => {
      fn.apply(this, args)
    }, delay)
  }
}

/**
 * 节流函数
 * @param {Function} fn 函数
 * @param {number} delay 延迟时间
 * @returns {Function}
 */
export function throttle(fn, delay = 300) {
  let lastTime = 0
  return function(...args) {
    const now = Date.now()
    if (now - lastTime >= delay) {
      lastTime = now
      fn.apply(this, args)
    }
  }
}
